import React, { useState } from 'react';

interface Indicator {
  name: string;
  value: string | number;
}

interface TradeRecommendation {
  id: string;
  symbol: string;
  action: 'BUY' | 'SELL' | 'HOLD';
  price: number;
  timestamp: string;
  confidence: number;
  stopLoss: number;
  takeProfit: number;
  reason: string;
  indicators: Indicator[];
}

interface RecommendationsListProps {
  recommendations: TradeRecommendation[];
  onExecute: (recommendation: TradeRecommendation) => void;
}

const RecommendationsList: React.FC<RecommendationsListProps> = ({ recommendations, onExecute }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionClass = (action: string) => {
    switch (action) {
      case 'BUY':
        return 'badge bg-success';
      case 'SELL':
        return 'badge bg-danger';
      case 'HOLD':
        return 'badge bg-warning';
      default:
        return 'badge bg-secondary';
    }
  };

  const getConfidenceClass = (confidence: number) => {
    if (confidence >= 80) return 'text-success';
    if (confidence >= 60) return 'text-warning';
    return 'text-danger';
  };

  if (recommendations.length === 0) {
    return (
      <div className="text-center p-3">
        <p className="text-muted">No hay recomendaciones disponibles</p>
      </div>
    );
  }

  return (
    <div className="recommendations-list">
      {recommendations.map((rec) => (
        <div 
          key={rec.id} 
          className={`recommendation-item card mb-3 ${expandedId === rec.id ? 'border-primary' : ''}`}
        >
          <div 
            className="card-header d-flex justify-content-between align-items-center" 
            onClick={() => toggleExpand(rec.id)}
            style={{ cursor: 'pointer' }}
          >
            <div>
              <span className={getActionClass(rec.action)}>{rec.action}</span>
              <span className="ms-2">{rec.symbol}</span>
            </div>
            <div>
              <span className={`fw-bold ${getConfidenceClass(rec.confidence)}`}>
                {rec.confidence}%
              </span>
              <i className={`ms-2 bi ${expandedId === rec.id ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
            </div>
          </div>
          
          {expandedId === rec.id && (
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-6">
                  <small className="text-muted d-block">Precio sugerido</small>
                  <strong>${rec.price.toFixed(2)}</strong>
                </div>
                <div className="col-6 text-end">
                  <small className="text-muted d-block">Timestamp</small>
                  <span>{formatTimestamp(rec.timestamp)}</span>
                </div>
              </div>
              
              <div className="row mb-3">
                <div className="col-6">
                  <small className="text-muted d-block">Stop Loss</small>
                  <span className="text-danger">${rec.stopLoss.toFixed(2)}</span>
                </div>
                <div className="col-6 text-end">
                  <small className="text-muted d-block">Take Profit</small>
                  <span className="text-success">${rec.takeProfit.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="mb-3">
                <small className="text-muted d-block">Análisis</small>
                <p className="mb-0">{rec.reason}</p>
              </div>
              
              {rec.indicators.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Indicadores</small>
                  <div className="row">
                    {rec.indicators.map((indicator, index) => (
                      <div key={index} className="col-6 mb-1">
                        <small className="d-block">
                          <span className="text-muted">{indicator.name}:</span> {indicator.value}
                        </small>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-center mt-3">
                <button 
                  className="btn btn-primary" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onExecute(rec);
                  }}
                >
                  Ejecutar Operación
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default RecommendationsList;
